import { getStored, setStored } from "@/lib/storage";

export type SessionSource = "electron" | "dev" | "paired" | "cloud";
export interface Session {
  apiUrl: string;
  token: string;
  source: SessionSource;
  /** Signed-in account, for cloud sessions. */
  email?: string;
}

/** Desk cloud API. The hosted web app (app.deskapp.dev) signs in here. */
export const CLOUD_URL = ((import.meta.env.VITE_DESK_CLOUD_URL as string | undefined) ?? "https://api.deskapp.dev").replace(/\/+$/, "");

/** True for the hosted web app, which only signs in to Desk cloud (it can't reach a Mac over plain HTTP). */
export const isHostedWeb = () => typeof location !== "undefined" && location.protocol === "https:" && !window.deskBridge;

interface CloudSession {
  apiUrl: string;
  token: string;
  email: string;
}

interface Pairing {
  apiUrl: string;
  token: string;
}

function normalizeUrl(url: string): string {
  return url.trim().replace(/\/+$/, "");
}

/**
 * Session resolution order:
 * 1. Electron preload bridge (`window.deskBridge.getSession()`).
 * 2. Vite dev middleware `GET /__desk/session` (same-origin, dev server only).
 * 3. A Desk cloud sign-in (localStorage).
 * 4. A pairing saved from the pairing screen (localStorage).
 */
export async function resolveSession(): Promise<Session | null> {
  const bridge = typeof window !== "undefined" ? window.deskBridge : undefined;
  if (bridge?.getSession) {
    try {
      const s = await bridge.getSession();
      if (s?.apiUrl && s?.token) return { apiUrl: normalizeUrl(s.apiUrl), token: s.token, source: "electron" };
    } catch {
      /* fall through */
    }
  }
  const paired = getStored<Pairing | null>("pairing", null);
  const cloud = getStored<CloudSession | null>("cloudSession", null);
  if (import.meta.env.DEV && !paired && !cloud) {
    try {
      const res = await fetch("/__desk/session", { headers: { Accept: "application/json" }, cache: "no-store" });
      if (res.ok) {
        const s = (await res.json()) as Pairing;
        if (s.apiUrl && s.token) return { apiUrl: normalizeUrl(s.apiUrl), token: s.token, source: "dev" };
      }
    } catch {
      /* fall through */
    }
  }
  if (cloud?.apiUrl && cloud?.token) return { apiUrl: normalizeUrl(cloud.apiUrl), token: cloud.token, source: "cloud", email: cloud.email };
  if (paired?.apiUrl && paired?.token) return { apiUrl: normalizeUrl(paired.apiUrl), token: paired.token, source: "paired" };
  return null;
}

export function savePairing(apiUrl: string, token: string) {
  setStored<Pairing>("pairing", { apiUrl: normalizeUrl(apiUrl), token: token.trim() });
}

export function clearPairing() {
  setStored("pairing", undefined);
}

export function saveCloudSession(apiUrl: string, token: string, email: string) {
  setStored<CloudSession>("cloudSession", { apiUrl: normalizeUrl(apiUrl), token, email });
}

export function clearCloudSession() {
  setStored("cloudSession", undefined);
}
